// E4.7 — replying on a platform-owned channel.
//
// ============================================================================
// THIS COMPONENT IS NOT THE SECURITY BOUNDARY
// ============================================================================
//
// The server applies `canReplyToThreadRow` on every submission: readable AND
// the channel's exact reply capability. Rendering this composer reflects that
// authority; it does not create it, and hiding it would not enforce anything.
//
// It is rendered only when the server has already told us the thread is
// reply-eligible, so an unauthorised person never sees a control at all — which
// matters because on a restricted channel the CONTROL itself would disclose
// that the thread exists and that replying is something they might be granted.
//
// ============================================================================
// THE KEY IS MINTED ONCE PER INTENT, NOT PER CLICK
// ============================================================================
//
// A person writing one reply is ONE intent, however many times the button is
// pressed or the network gives up. So the key is minted when the draft starts
// and reused for every attempt; only a genuinely new reply gets a new one.
//
// Minting inside the submit handler would make a double click two keys, and
// two keys are two emails to a member of the public. That is the single most
// important line in this file.
//
// ============================================================================
// WHAT THE PERSON IS TOLD
// ============================================================================
//
// `accepted` is never rendered as "Sent". Central holding the reply is not a
// provider having delivered it, and an agent who reads "Sent" stops watching.
//
// `outcome_unknown` gets no retry button. The send may have happened; a retry
// control here is an invitation to send a second copy of a privacy reply to
// someone who may already have the first.
import React, { useCallback, useRef, useState } from 'react';
import { AlertCircle, Clock, HelpCircle, Send } from 'lucide-react';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'accepted'; deduplicated: boolean }
  | { kind: 'failed'; code?: string }
  | { kind: 'outcome_unknown' }
  | { kind: 'error'; message: string };

export interface SystemChannelReplyComposerProps {
  readonly threadId: string;
  /** The correspondent, for display only. The server derives the real one. */
  readonly recipientLabel: string;
  readonly onReplyAccepted?: () => void;
}

export const SystemChannelReplyComposer: React.FC<SystemChannelReplyComposerProps> = ({
  threadId,
  recipientLabel,
  onReplyAccepted,
}) => {
  const [bodyText, setBodyText] = useState('');
  const [state, setState] = useState<SubmitState>({ kind: 'idle' });

  // ONE key per intent. Minted here, on first need, and held across retries.
  const intentKey = useRef<string | null>(null);
  if (intentKey.current === null) intentKey.current = crypto.randomUUID();

  const pending = state.kind === 'pending';
  const settled = state.kind === 'accepted' || state.kind === 'outcome_unknown';

  const submit = useCallback(async function submit() {
    if (pending || settled) return;
    if (!bodyText.trim()) return;
    setState({ kind: 'pending' });
    try {
      const res = await fetch(`/api/unibox/threads/${threadId}/system-channel-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // The whole input surface. No `to`, no `from`, no `cc`, no subject —
        // the server derives every one of those from canonical rows.
        body: JSON.stringify({ bodyText, idempotencyKey: intentKey.current }),
      });
      const payload = (await res.json().catch(() => ({}))) as {
        status?: string;
        deduplicated?: boolean;
        code?: string;
        error?: string;
      };

      if (res.status === 202 && payload.status === 'accepted') {
        setState({ kind: 'accepted', deduplicated: payload.deduplicated === true });
        onReplyAccepted?.();
        return;
      }
      if (payload.status === 'outcome_unknown') {
        setState({ kind: 'outcome_unknown' });
        return;
      }
      if (payload.status === 'failed') {
        setState({ kind: 'failed', code: payload.code });
        return;
      }
      setState({ kind: 'error', message: payload.error ?? 'Reply could not be sent' });
    } catch {
      // The request never completed, so we do not know whether it landed.
      // Treated as the ambiguous state rather than a failure, and the SAME key
      // is retained so a later attempt is the same intent.
      setState({ kind: 'outcome_unknown' });
    }
  }, [bodyText, onReplyAccepted, pending, settled, threadId]);

  return (
    <div className="border-t border-gray-200 p-3">
      <div className="mb-2 text-xs text-gray-500">
        Replying to <span className="font-medium">{recipientLabel}</span> from this channel
      </div>

      <textarea
        className="w-full resize-y rounded border border-gray-300 p-2 text-sm"
        rows={4}
        value={bodyText}
        onChange={(e) => setBodyText(e.target.value)}
        disabled={pending || settled}
        placeholder="Write a reply…"
        aria-label="Reply body"
      />

      <div className="mt-2 flex items-center justify-between gap-3">
        <StatusLine state={state} />
        <button
          type="button"
          onClick={submit}
          disabled={pending || settled || !bodyText.trim()}
          className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          {pending ? 'Sending…' : 'Send reply'}
        </button>
      </div>
    </div>
  );
};

const StatusLine: React.FC<{ state: SubmitState }> = ({ state }) => {
  switch (state.kind) {
    case 'accepted':
      // NOT "Sent". Central has it; no provider has run.
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-blue-600">
          <Clock className="h-3.5 w-3.5" />
          {state.deduplicated
            ? 'Already accepted — not yet sent'
            : 'Accepted — not yet sent'}
        </span>
      );
    case 'outcome_unknown':
      // Amber and explicit. No retry control: the send may have happened, and
      // a second attempt would be a second email to a member of the public.
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-amber-600">
          <HelpCircle className="h-3.5 w-3.5" />
          Outcome unknown — may have been sent. Check before replying again.
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle className="h-3.5 w-3.5" />
          Not sent{state.code ? ` (${state.code})` : ''}
        </span>
      );
    case 'error':
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle className="h-3.5 w-3.5" />
          {state.message}
        </span>
      );
    default:
      return <span />;
  }
};

export default SystemChannelReplyComposer;
